<script setup>
// Vue Imports
import { computed, reactive, ref, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useVuelidate } from '@vuelidate/core'
import { required, email, minLength, maxLength, numeric, sameAs, helpers } from '@vuelidate/validators'

// Component Imports
import AtomButton from "@Atoms/Button.vue"

// Utils Imports
import { updateClientPreferences } from "@/utils/cognito"
import { evaluateStrength } from '@/utils/passwordStrength'
import { storeCurrentSession } from "@/utils/cognito"

// Store Imports
import { useStoreDismissedMessages } from "@/stores/storeDismissedMessages";
// Other Imports
import { signIn, confirmSignIn } from "aws-amplify/auth"
import useAlert from "@/hooks/alert"
import { XCircleIcon, CheckCircleIcon } from '@heroicons/vue/24/solid'
import * as Sentry from "@sentry/vue";

// Component Logic
let loading = ref(false)

// creates a reference to Vue router
const router = useRouter()
const route = useRoute()

const loginForm = reactive({
  username: route.query.username || '',
  password: ''
})

// set the validation rules
const loginFormRules = computed(() => {
  return {
    username: {
      required: helpers.withMessage('Please enter your username', required),
      email: helpers.withMessage('Please enter a valid email address', email),
    },
    password: {
      required: helpers.withMessage('Please enter your password', required),
      minLength: helpers.withMessage('Your password should have 7 or more characters', minLength(7)),
    }
  }
})
const v$ = useVuelidate(loginFormRules, loginForm)

// stores reactive ref to password inputs
const changePasswordForm = reactive({
  newPassword: '',
  confirmNewPassword: ''
})
// set the validation rules
// Cognito password regex - see http://awspasswordregex.s3-website-eu-west-1.amazonaws.com/
const cognitoPassword = helpers.regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[\^$*.\[\]{}\(\)?\-\"!@#%&\/,><\':;|_~`])\S{8,99}$/)
//const cognitoPassword = (value) => value.match(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[\^$*.\[\]{}\(\)?\-\"!@#%&\/,><\':;|_~`])\S{8,99}$/);

const changePasswordFormRules = computed(() => {
  return {
    newPassword: {
      required: helpers.withMessage('Please enter your new password ', required),
      cognitoPassword: helpers.withMessage('Please enter a valid password at least 8 characters in length consisting of at least one uppercase letter, one lowercase letter, one number and one special character', cognitoPassword)
    },
    confirmNewPassword: {
      required: helpers.withMessage('Please enter your new password again ', required),
      sameAs: sameAs(changePasswordForm.newPassword),
    },
  }
})
const v2$ = useVuelidate(changePasswordFormRules, changePasswordForm)

const mfaForm = reactive({
  code: ''
})

// set the validation rules
const mfaFormRules = computed(() => {
  return {
    code: {
      required: helpers.withMessage('Please enter the 6-digit code we just sent you', required),
      numeric: helpers.withMessage('Please only enter numbers into this field', numeric),
      minLength: minLength(6),
      maxLength: maxLength(6),
    }
  }
})
const v3$ = useVuelidate(mfaFormRules, mfaForm)

//sets up hook for message alerting
const { message, messageStyleType, setMessage } = useAlert()

const confirmMFACode = ref(false)
const newPasswordRequired = ref(false)
const verifyDateOfBirth = ref(false);

if(route.query.regSuccess){
  setMessage("You have successfully registered. Please log in using the form below.", "success")
}
if(route.query.accountConfirmed){
  setMessage("You have successfully confirmed your account. Please log in to continue.", "success")
}
if(route.query.loggedOut){
  setMessage("You've successfully logged out", "success")
}
if(route.query.passwordResetSuccess){
  setMessage("You have successfully reset your password. Please log in to continue.", "success")
}
if(route.query.timedOut){
  setMessage("Your session has expired. Please sign in again below.", "warning")
}


async function submitLogin() {
  // runs form validation code
  const validationResult = await v$.value.$validate()
  if (!validationResult) {
    return false
  }

  if (loading.value === true) return
  loading.value = true

  await handleSignIn({
    username: loginForm.username,
    password: loginForm.password
  })
}

async function handleSignInNextStep(nextStep) {
  loading.value = false;

  switch(nextStep) {
    case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
      newPasswordRequired.value = true
      setMessage("Please change your temporary password using the form below.", "warning")

      break
    case "CONFIRM_SIGN_IN_WITH_SMS_CODE":
      setMessage(null)
      confirmMFACode.value = true
      newPasswordRequired.value = false
      break;
    case "DONE":
      // Update the state with the session tokens
      await storeCurrentSession()

      //await verifyBirthdate()
      // We need to refresh the tokens to include new permissions
      //await handleSessionRefresh()
      const messageDismissal = useStoreDismissedMessages()
      messageDismissal.clearDismissedMessages()

      if (route.query.marketing != null) {
        await updateClientPreferences("MARKETING_UPDATES", route.query.marketing, true)
      }

      await router.replace({
        name: "Dashboard",
      })

      break
    default:
      console.log(nextStep.signInStep)
      return false
  }

  return true
}

async function submitNewPassword() {
  // runs form validation code
  const validationResult2 = await v2$.value.$validate()
  if (!validationResult2) {
    return false
  }

  loading.value = true

  const { nextStep } = await confirmSignIn({
    challengeResponse: changePasswordForm.newPassword
  })

  const nextStepResult = handleSignInNextStep(nextStep.signInStep)

  if (nextStepResult === false) {
    loading.value = false

    Sentry.captureException(new Error(`User's password could not be updated. Expected nextStep.signInStep to be 'DONE', but nextStep was ${JSON.stringify(nextStep)}`))

    setMessage("Your password could not be updated. Please try again.", "warning")
  }
}

async function submitMfaCode() {
  // runs form validation code
  const validationResult = await v3$.value.$validate()
  if (!validationResult) {
    return false
  }

  loading.value = true

  try {
    const { isSignedIn } = await confirmSignIn({
      challengeResponse: mfaForm.code
    })

    if(isSignedIn){
      await storeCurrentSession()

      await router.replace({
        name: "Dashboard",
      })
    }

  } catch ({message}) {
    loading.value = false
    setMessage(message, 'danger')
    console.log(message)
  }
}

async function handleSignIn({ username, password }) {
  try {
    const { nextStep } = await signIn({ username, password })

    //signInStep:
    // "CONFIRM_SIGN_IN_WITH_TOTP_CODE"
    // "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE"
    // "CONTINUE_SIGN_IN_WITH_MFA_SELECTION"
    // "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
    // "CONFIRM_SIGN_IN_WITH_SMS_CODE"
    // "CONTINUE_SIGN_IN_WITH_TOTP_SETUP"
    // "CONFIRM_SIGN_UP"
    // "RESET_PASSWORD"
    // "DONE"

    loading.value = false

    handleSignInNextStep(nextStep.signInStep)
  } catch (error) {
    loading.value = false
    setMessage(error.message, "warning")
  }
}

/* Password strength */
const passwordChecks = reactive({
  characters: false,
  numbers: false,
  uppercase: false,
  lowercase: false,
  special: false
})

const passwordStrength = computed(() => {
  return evaluateStrength(changePasswordForm.newPassword)
})

watch(() => changePasswordForm.newPassword, password => {
  passwordChecks.characters = password.length > 7 && password.length < 51
  passwordChecks.numbers = /\d/.test(password)
  passwordChecks.uppercase = /[A-Z]/.test(password)
  passwordChecks.lowercase = /[a-z]/.test(password)
  passwordChecks.special = /[\^$*.[\]{}()?"!@#%&\/\\,><':|_~`=+\- ]/.test(password)
})
</script>

<template>
  <section class="bg-white dark:bg-teal py-20 lg:py-28 px-8 xl:px-0">
    <div class="mx-auto max-w-7xl flex flex-col items-center justify-center gap-12">
      <base-message
        :type="messageStyleType"
        v-if="message"
        class="sm:mx-auto sm:w-full sm:max-w-lg"
      >
        {{ message }}
      </base-message>

      <div v-if="newPasswordRequired" class="text-center">
        <h1 class="text-granite dark:text-white">Reset Password</h1>
        <div class="std-text">Reset your password below</div>
      </div>

      <div v-else-if="confirmMFACode" class="text-center">
        <h1 class="text-granite dark:text-white">Confirm Code</h1>
        <div class="std-text">Please enter the code we just sent to you via SMS</div>
      </div>

      <div v-else-if="verifyDateOfBirth" class="text-center">
        <h1 class="text-granite dark:text-white">Confirm your Date of Birth</h1>
        <div class="std-text">Please enter your details below</div>
      </div>

      <div v-else class="text-center">
        <h1 class="text-granite dark:text-white">Log into your account</h1>
      </div>

      <div v-if="newPasswordRequired">
        <form @submit.prevent="submitNewPassword" class="space-y-8 max-w-screen-sm">
          <div>
            <input
              type="password"
              class="login-form-input body-3-reg"
              id="newPassword"
              placeholder="New Password"
              v-model.trim="changePasswordForm.newPassword"
              autocomplete="false"
            >
            <div
              v-if="v2$.newPassword.$errors.length"
              class="my-3 rounded-lg bg-red-50 p-3 w-full"
            >
              <span
                v-for="error in v2$.newPassword.$errors"
                :key="error.$uid"
                class="text-red-400"
              >
                {{ error.$message }}
              </span>
            </div>
          </div>

          <div ref="passwordFilters" class="flex flex-col gap-2 body-3-reg">
            <div class="flex items-center border rounded-md border-granite/50 dark:border-white justify-start overflow-hidden">
              <Transition name="width">
                <div
                  :class="
                    passwordStrength[0] === 1 ? 'w-[10%] bg-red-400'
                    : passwordStrength[0] === 2 ? 'w-[20%] bg-red-400'
                    : passwordStrength[0] === 3 ? 'w-[30%] bg-red-400'
                    : passwordStrength[0] === 4 ? 'w-[40%] bg-red-400'
                    : passwordStrength[0] === 5 ? 'w-[50%] bg-yellow-400'
                    : passwordStrength[0] === 6 ? 'w-[60%] bg-yellow-400'
                    : passwordStrength[0] === 7 ? 'w-[70%] bg-green-400'
                    : passwordStrength[0] === 8 ? 'w-[80%] bg-green-400'
                    : passwordStrength[0] === 9 ? 'w-full bg-green-600'
                    : 'w-full bg-green-600'
                  "
                  class="px-2 pt-1 pb-2 h-[30px] text-xs transition-width ease-in-out duration-300 text-black"
                >
                  <p class="absolute">
                    {{passwordStrength[1]}}
                  </p>
                </div>
              </Transition>
            </div>

            <div class="flex flex-col gap-2">
              <div class="flex items-center gap-2 dropdown-content mt-2">
                <CheckCircleIcon v-if="passwordChecks.characters" class="w-4 text-green-500 mt-1" />
                <XCircleIcon v-else class="w-4 mt-1" />
                <p class="text-sm dark:text-white">8 to 50 characters.</p>
              </div>
              <div class="flex items-center gap-2 dropdown-content">
                <CheckCircleIcon v-if="passwordChecks.numbers" class="w-4 text-green-500 mt-1" />
                <XCircleIcon v-else class="w-4 mt-1" />
                <p class="text-sm dark:text-white">1 number or more.</p>
              </div>
              <div class="flex items-center gap-2 dropdown-content">
                <CheckCircleIcon v-if="passwordChecks.uppercase" class="w-4 text-green-500 mt-1" />
                <XCircleIcon v-else class="w-4 mt-1" />
                <p class="text-sm dark:text-white">1 upper case letter or more.</p>
              </div>
              <div class="flex items-center gap-2 dropdown-content">
                <CheckCircleIcon v-if="passwordChecks.lowercase" class="w-4 text-green-500 mt-1" />
                <XCircleIcon v-else class="w-4 mt-1" />
                <p class="text-sm dark:text-white">1 lower case letter or more.</p>
              </div>
              <div class="flex items-center gap-2 dropdown-content mb-2">
                <CheckCircleIcon v-if="passwordChecks.special" class="w-4 text-green-500 mt-1" />
                <XCircleIcon v-else class="w-4 mt-1" />
                <p class="text-sm dark:text-white">1 special character or more. Allowed characters are:<br/> ^ $ * . [ ] { } ( ) ? " ! @ # % &amp; / \ , &gt; &lt; ' : ; | _ ~ ` = + - </p>
              </div>
            </div>
          </div>

          <div>
            <input
              type="password"
              class="login-form-input body-3-reg"
              id="confirm-password"
              placeholder="Confirm Password"
              v-model.trim="changePasswordForm.confirmNewPassword"
              autocomplete="false"
            >
            <div
              v-if="v2$.confirmNewPassword.$errors.length"
              class="my-3 rounded-lg bg-red-50 p-3 w-full"
            >
              <span
                v-for="error in v2$.confirmNewPassword.$errors"
                :key="error.$uid"
                class="text-red-400"
              >
                {{ error.$message }}
              </span>
            </div>
          </div>

          <div class="text-center">
            <AtomButton
              :isLoading="loading"
              type="submit"
              aria-label="reset password button"
              button-type="unset"
              class="flex flex-row justify-center items-center body-3-bold large-primary button-mobile lg:button-desktop rounded-full mx-auto lg:mx-0 w-max"
            >
              Reset Password
            </AtomButton>
          </div>
        </form>
      </div>

      <div v-else-if="confirmMFACode">
        <form @submit.prevent="submitMfaCode" class="space-y-8">
          <div>
            <input
              type="text"
              class="login-form-input body-3-reg"
              id="mfaCode"
              placeholder="MFA Code"
              v-model.trim="mfaForm.code"
              autocomplete="false"
            >
            <div
              v-if="v$.password.$errors.length"
              class="my-3 rounded-lg bg-red-50 p-3 w-full"
            >
              <span
                v-for="error in v$.password.$errors"
                :key="error.$uid"
                class="text-red-400"
              >
                {{ error.$message }}
              </span>
            </div>
          </div>

          <div class="text-center">
            <AtomButton
              :isLoading="loading"
              type="submit"
              aria-label="confirm mfa code button"
              button-type="unset"
              class="flex flex-row justify-center items-center body-3-bold large-primary button-mobile lg:button-desktop rounded-full mx-auto lg:mx-0 w-max"
            >
              Confirm MFA Code
            </AtomButton>
          </div>
        </form>
      </div>

      <div v-else class="flex flex-col space-y-8 w-full">
        <!-- Submit login form -->
        <form @submit.prevent="submitLogin" class="flex flex-col gap-y-8 mx-auto min-w-sm sm:w-96">
          <div>
            <input
              type="text"
              class="login-form-input body-3-reg"
              id="username"
              placeholder="Email"
              v-model.trim="loginForm.username"
              autocomplete
            >
            <div
              v-if="v$.username.$errors.length"
              class="my-3 rounded-lg bg-red-50 p-3"
            >
            <span
              v-for="error in v$.username.$errors"
              :key="error.$uid"
              class="text-red-400"
            >
              {{ error.$message }}
            </span>
            </div>
          </div>

          <div>
            <input
              type="password"
              class="login-form-input body-3-reg"
              id="password"
              placeholder="Password"
              v-model.trim="loginForm.password"
              autocomplete
              required
            >
            <div
              v-if="v$.password.$errors.length"
              class="my-3 rounded-lg bg-red-50 p-3"
            >
              <span
                v-for="error in v$.password.$errors"
                :key="error.$uid"
                class="text-red-400"
              >
                {{ error.$message }}
              </span>
            </div>

            <div class="flex flex-row justify-end text-end">
              <AtomButton
                element="RouterLink"
                to="/forgotpassword"
                aria-label="forgot password button"
                button-type="unset"
                class="tertiary-mwp body-3-semi-bold mt-2 underline mx-auto"
              >
                Forgot password?
              </AtomButton>
            </div>
          </div>

          <div class="flex justify-center">
            <AtomButton
              :isLoading="loading"
              type="submit"
              aria-label="Log in button"
              button-type="unset"
              :class="[
                'primary-mwp h-full button-padding body-3-semi-bold rounded-full',
                { 'flex items-center justify-center' : loading }
              ]"
            >
              Log In
            </AtomButton>
          </div>
        </form>

        <p class="text-center body-3-reg text-forest/75 dark:text-white">
          If you experience any issues with registering for your online account, please call our service desk on <span class="font-bold">0800 028 3200</span> and we will be happy to assist.
        </p>
      </div>
    </div>
  </section>
</template>
